import { z } from 'zod';

import type { OrderTemplate, OrderTemplateCreateInput, OrderTemplateUpdateInput } from './api';
import { menuLineFormSchema, type MenuLineFormValues } from './menu-line-form-schema';

/**
 * Mirrors `apps/api` `createOrderTemplateSchema`/`updateOrderTemplateSchema`
 * (`order-template.dto.ts`), same convention as `orderFormSchema`.
 * `daysOfWeek` stays a `string[]` of checkbox values here — `Checkbox`
 * renders native DOM checkboxes, whose `value` always arrives as a
 * string regardless of the `number` passed as the JSX prop — and is
 * coerced to `number[]` only in `toOrderTemplateCreateInput`/
 * `toOrderTemplateUpdateInput`, never trusted as already numeric from
 * the form state. `items` is one row per active position
 * (`rotating-menu/design.md` §4), no minimum: a template with every
 * position at zero quantity is a valid (if useless) draft, same as
 * before this change.
 */
export const orderTemplateFormSchema = z.object({
  customerId: z.string().uuid({ message: 'Seleccioná una empresa' }),
  siteId: z.string().uuid({ message: 'Seleccioná una sede' }),
  // Empty unless the customer holds contracts in several points of sale (the modal decides).
  operatingUnitId: z.string(),
  name: z.string().trim().min(1, 'El nombre es obligatorio').max(200),
  daysOfWeek: z.array(z.string()).min(1, 'Elegí al menos un día de la semana'),
  items: z.array(menuLineFormSchema),
  isActive: z.boolean(),
});

export type OrderTemplateFormValues = z.infer<typeof orderTemplateFormSchema>;

export const DEFAULT_ORDER_TEMPLATE_FORM_VALUES: OrderTemplateFormValues = {
  customerId: '',
  siteId: '',
  operatingUnitId: '',
  name: '',
  daysOfWeek: [],
  items: [],
  isActive: true,
};

export function toOrderTemplateFormValues(template: OrderTemplate): OrderTemplateFormValues {
  return {
    customerId: template.customerId,
    siteId: template.siteId,
    operatingUnitId: template.operatingUnitId,
    name: template.name,
    daysOfWeek: template.daysOfWeek.map(String),
    items: template.items.map((item) => ({
      positionId: item.positionId,
      quantity: String(item.quantity),
      notes: item.notes ?? '',
    })),
    isActive: template.isActive,
  };
}

function toItemInputs(items: MenuLineFormValues[]): OrderTemplateCreateInput['items'] {
  return items
    .filter((item) => item.quantity !== '' && Number(item.quantity) > 0)
    .map((item) => ({
      positionId: item.positionId,
      quantity: Number(item.quantity),
      notes: item.notes === '' ? null : item.notes,
    }));
}

export function toOrderTemplateCreateInput(
  values: OrderTemplateFormValues,
): OrderTemplateCreateInput {
  return {
    customerId: values.customerId,
    siteId: values.siteId,
    ...(values.operatingUnitId === '' ? {} : { operatingUnitId: values.operatingUnitId }),
    name: values.name,
    daysOfWeek: values.daysOfWeek.map(Number),
    items: toItemInputs(values.items),
  };
}

export function toOrderTemplateUpdateInput(
  values: OrderTemplateFormValues,
): OrderTemplateUpdateInput {
  return {
    name: values.name,
    daysOfWeek: values.daysOfWeek.map(Number),
    isActive: values.isActive,
    items: toItemInputs(values.items),
  };
}
